import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Public Registration Page
 *
 * Allows users to complete their registration using a unique token
 * User only needs to set a password, all other info is pre-filled by admin
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { FaCheckCircle, FaTimesCircle, FaSpinner, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { apiClient } from '../utils/apiClient';
import { useAuthStore } from '../stores/authStore';
// Force light theme for this page
const LightTheme = createGlobalStyle `
  :root {
    --bg-primary: #ffffff;
    --bg-secondary: #ffffff;
    --bg-tertiary: #f9fafb;
    --text-primary: #111827;
    --text-secondary: #6b7280;
    --text-tertiary: #9ca3af;
    --border-color: #e5e7eb;
    --border-color-hover: #d1d5db;
  }
`;
const PublicRegistration = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setTokens, setUser } = useAuthStore();
    const [step, setStep] = useState('validating');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Registration data from token
    const [registrationData, setRegistrationData] = useState(null);
    // Form data
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    // Password strength
    const [passwordStrength, setPasswordStrength] = useState(null);
    useEffect(() => {
        if (token) {
            validateToken();
        }
        else {
            setError('Invalid registration link - missing token');
            setStep('error');
        }
    }, [token]);
    const validateToken = async () => {
        try {
            const response = await apiClient.post('pre-registration-validate', { token }, { skipAuth: true });
            if (response.ok && response.data?.success && response.data.valid) {
                setRegistrationData(response.data.data);
                setStep('form');
            }
            else {
                setError(response.data?.error || 'Invalid or expired registration link');
                setStep('error');
            }
        }
        catch (err) {
            setError(err.message || 'Failed to validate registration link');
            setStep('error');
        }
    };
    const checkPasswordStrength = (pass) => {
        if (pass.length < 8) {
            setPasswordStrength('weak');
            return;
        }
        const hasUpperCase = /[A-Z]/.test(pass);
        const hasLowerCase = /[a-z]/.test(pass);
        const hasNumbers = /\d/.test(pass);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
        const strengthScore = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
        if (strengthScore >= 4 && pass.length >= 12) {
            setPasswordStrength('strong');
        }
        else if (strengthScore >= 3 && pass.length >= 10) {
            setPasswordStrength('medium');
        }
        else {
            setPasswordStrength('weak');
        }
    };
    const handlePasswordChange = (value) => {
        setPassword(value);
        if (value) {
            checkPasswordStrength(value);
        }
        else {
            setPasswordStrength(null);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }
        if (!acceptTerms) {
            setError('You must accept the terms and conditions');
            return;
        }
        setIsSubmitting(true);
        setError('');
        try {
            console.log('Completing pre-registration with token:', token);
            console.log('Token length:', token?.length);
            console.log('Password length:', password?.length);
            const response = await apiClient.post('pre-registration-complete', { token, password }, { skipAuth: true });
            console.log('Pre-registration response:', response);
            if (response.ok && response.data?.success) {
                console.log('Registration successful, tokens received');
                // Auto-login the user
                if (response.data.accessToken && response.data.refreshToken) {
                    setTokens(response.data.accessToken, response.data.refreshToken);
                    if (response.data.user) {
                        setUser(response.data.user);
                    }
                    setStep('success');
                    // Redirect to platform after 2 seconds
                    setTimeout(() => {
                        navigate('/platform');
                    }, 2000);
                }
                else {
                    console.error('No tokens in successful response');
                    setError('Registration succeeded but login failed. Please try logging in manually.');
                }
            }
            else {
                console.error('Registration failed:', response.data?.error);
                setError(response.data?.error || `Failed to complete registration (Status: ${response.status})`);
            }
        }
        catch (err) {
            console.error('Exception during registration:', err);
            setError(err.message || 'Failed to complete registration');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    if (step === 'validating') {
        return (_jsxs(Container, { children: [_jsx(LightTheme, {}), _jsxs(Card, { children: [_jsx(IconContainer, { children: _jsx(FaSpinner, { className: "spinner" }) }), _jsx(Title, { children: "Validating Registration Link..." }), _jsx(Description, { children: "Please wait while we verify your registration link" })] })] }));
    }
    if (step === 'error') {
        return (_jsxs(Container, { children: [_jsx(LightTheme, {}), _jsxs(Card, { children: [_jsx(IconContainer, { children: _jsx(FaTimesCircle, { style: { color: '#ef4444' } }) }), _jsx(Title, { children: "Registration Link Invalid" }), _jsx(Description, { children: error }), _jsxs(ErrorHelp, { children: [_jsx("p", { children: "Possible reasons:" }), _jsxs("ul", { children: [_jsx("li", { children: "Link has expired (links are valid for 7 days)" }), _jsx("li", { children: "Link has already been used" }), _jsx("li", { children: "Link is malformed or incomplete" })] }), _jsx("p", { children: "Please contact your administrator for a new registration link." })] })] })] }));
    }
    if (step === 'success') {
        return (_jsxs(Container, { children: [_jsx(LightTheme, {}), _jsxs(Card, { children: [_jsx(IconContainer, { children: _jsx(FaCheckCircle, { style: { color: '#22c55e' } }) }), _jsx(Title, { children: "Registration Complete!" }), _jsxs(Description, { children: ["Welcome, ", registrationData?.firstName, "! Your account has been created successfully."] }), _jsx(SuccessMessage, { children: "Redirecting you to the platform..." })] })] }));
    }
    return (_jsxs(Container, { children: [_jsx(LightTheme, {}), _jsxs(Card, { children: [_jsx(IconContainer, { children: _jsx(FaLock, { style: { color: '#3b82f6' } }) }), _jsx(Title, { children: "Complete Your Registration" }), _jsxs(Description, { children: ["Welcome, ", registrationData?.firstName, " ", registrationData?.lastName, "!", _jsx("br", {}), "Set your password to activate your account."] }), _jsxs(InfoBox, { children: [_jsxs(InfoRow, { children: [_jsx(Label, { children: "Email:" }), _jsx(Value, { children: registrationData?.email })] }), _jsxs(InfoRow, { children: [_jsx(Label, { children: "Business:" }), _jsx(Value, { children: registrationData?.businessName || 'N/A' })] }), _jsxs(InfoRow, { children: [_jsx(Label, { children: "Link Expires:" }), _jsx(Value, { children: new Date(registrationData?.expiresAt).toLocaleString() })] })] }), _jsxs(Form, { onSubmit: handleSubmit, children: [error && (_jsx(ErrorBox, { children: error })), _jsxs(FormGroup, { children: [_jsx(InputLabel, { children: "Create Password *" }), _jsxs(PasswordInputWrapper, { children: [_jsx(Input, { type: showPassword ? 'text' : 'password', value: password, onChange: e => handlePasswordChange(e.target.value), placeholder: "Enter your password", required: true }), _jsx(PasswordToggle, { onClick: () => setShowPassword(!showPassword), children: showPassword ? _jsx(FaEyeSlash, {}) : _jsx(FaEye, {}) })] }), passwordStrength && (_jsxs(PasswordStrength, { strength: passwordStrength, children: [passwordStrength === 'weak' && '❌ Weak - Add more characters and variety', passwordStrength === 'medium' && '⚠️ Medium - Could be stronger', passwordStrength === 'strong' && '✅ Strong password'] })), _jsx(HelpText, { children: "Minimum 8 characters, use a mix of letters, numbers, and symbols" })] }), _jsxs(FormGroup, { children: [_jsx(InputLabel, { children: "Confirm Password *" }), _jsxs(PasswordInputWrapper, { children: [_jsx(Input, { type: showConfirmPassword ? 'text' : 'password', value: confirmPassword, onChange: e => setConfirmPassword(e.target.value), placeholder: "Confirm your password", required: true }), _jsx(PasswordToggle, { onClick: () => setShowConfirmPassword(!showConfirmPassword), children: showConfirmPassword ? _jsx(FaEyeSlash, {}) : _jsx(FaEye, {}) })] })] }), _jsxs(CheckboxGroup, { children: [_jsx(Checkbox, { type: "checkbox", id: "terms", checked: acceptTerms, onChange: e => setAcceptTerms(e.target.checked) }), _jsxs(CheckboxLabel, { htmlFor: "terms", children: ["I agree to the ", _jsx("a", { href: "/terms", target: "_blank", children: "Terms and Conditions" }), " and ", _jsx("a", { href: "/privacy", target: "_blank", children: "Privacy Policy" })] })] }), _jsx(SubmitButton, { type: "submit", disabled: isSubmitting || !acceptTerms, children: isSubmitting ? 'Creating Account...' : 'Create Account & Sign In' })] })] })] }));
};
// Styled Components
const Container = styled.div `
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--bg-primary);
`;
const Card = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  padding: 48px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;
const IconContainer = styled.div `
  text-align: center;
  margin-bottom: 24px;

  svg {
    font-size: 64px;

    &.spinner {
      animation: spin 1s linear infinite;
      color: #3b82f6;
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
const Title = styled.h1 `
  text-align: center;
  color: var(--text-primary);
  font-size: 28px;
  margin: 0 0 16px 0;
  font-weight: 600;
`;
const Description = styled.p `
  text-align: center;
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;
const InfoBox = styled.div `
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 32px;
`;
const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const Label = styled.span `
  color: var(--text-tertiary);
  font-size: 14px;
`;
const Value = styled.span `
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
`;
const Form = styled.form `
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const FormGroup = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const InputLabel = styled.label `
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
`;
const PasswordInputWrapper = styled.div `
  position: relative;
  display: flex;
  align-items: center;
`;
const Input = styled.input `
  width: 100%;
  padding: 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  border-radius: 4px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
const PasswordToggle = styled.button `
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: var(--text-primary);
  }

  svg {
    font-size: 18px;
  }
`;
const PasswordStrength = styled.div `
  font-size: 13px;
  margin-top: 4px;
  color: ${props => {
    switch (props.strength) {
        case 'weak': return '#ef4444';
        case 'medium': return '#f59e0b';
        case 'strong': return '#22c55e';
    }
}};
`;
const HelpText = styled.p `
  margin: 0;
  font-size: 12px;
  color: var(--text-tertiary);
`;
const CheckboxGroup = styled.div `
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;
const Checkbox = styled.input `
  margin-top: 3px;
  cursor: pointer;
`;
const CheckboxLabel = styled.label `
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;

  a {
    color: #3b82f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;
const SubmitButton = styled.button `
  width: 100%;
  padding: 14px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
  }
`;
const ErrorBox = styled.div `
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  border-radius: 4px;
  padding: 12px;
  color: #ef4444;
  font-size: 14px;
  text-align: center;
`;
const ErrorHelp = styled.div `
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid #ef4444;
  border-radius: 4px;
  padding: 20px;
  margin-top: 24px;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;

  p {
    margin: 0 0 12px 0;
    color: var(--text-primary);
    font-weight: 500;
  }

  ul {
    margin: 0 0 16px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }
`;
const SuccessMessage = styled.div `
  text-align: center;
  color: #22c55e;
  font-size: 16px;
  margin-top: 24px;
  font-weight: 500;
`;
export default PublicRegistration;
